import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/blocks/Navbar";
import { Footer } from "@/components/blocks/footer";
import { Seo } from "@/components/Seo";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPostSummary {
  id: string;
  title: string;
  slug: string;
  meta_description: string | null;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  published_at: string;
}

const footerData = {
  logo: <img src="/lovable-uploads/c22d7cd3-d7b2-4b69-9087-2e231ebb8feb.png" alt="Quickatoo Logo" className="h-10 w-auto" />,
  brandName: "Quickatoo",
  socialLinks: [],
  mainLinks: [{ href: "/", label: "Home" }, { href: "/blog", label: "Blog" }],
  legalLinks: [{ href: "/privacy", label: "Privacy" }, { href: "/terms", label: "Terms" }],
  copyright: { text: "© 2024 Quickatoo", license: "All rights reserved" },
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

const Blog = () => {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, meta_description, hero_image_url, hero_image_alt, published_at")
        .eq("status", "published")
        .order("published_at", { ascending: false });

      if (error) console.error("Failed to load blog posts:", error.message);
      setPosts((data as BlogPostSummary[]) ?? []);
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <Seo
        title="Blog — Painting Business Tips & Guides | Quickatoo"
        description="Practical guides on quoting, pricing and managing painting jobs — from the team behind Quickatoo, the painters estimator."
        path="/blog"
      />
      <Navbar />
      <main className="min-h-screen bg-background pt-32 pb-20">
        <div className="container mx-auto max-w-5xl px-4">
          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Quickatoo Blog</h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Guides, tips and insights for painting professionals.
            </p>
          </header>

          {loading ? (
            <p className="text-center text-muted-foreground">Loading articles…</p>
          ) : posts.length === 0 ? (
            <p className="text-center text-muted-foreground">No articles published yet — check back soon.</p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    {post.hero_image_url && (
                      <img
                        src={post.hero_image_url}
                        alt={post.hero_image_alt ?? post.title}
                        loading="lazy"
                        className="h-52 w-full object-cover"
                      />
                    )}
                    <div className="p-6">
                      <time className="text-sm text-muted-foreground" dateTime={post.published_at}>
                        {formatDate(post.published_at)}
                      </time>
                      <h2 className="mt-2 text-xl font-semibold text-foreground">{post.title}</h2>
                      {post.meta_description && (
                        <p className="mt-3 text-muted-foreground line-clamp-3">{post.meta_description}</p>
                      )}
                      <span className="mt-4 inline-block font-medium text-primary">Read article →</span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer {...footerData} />
    </>
  );
};

export default Blog;
